/**
 * SettingsPage — Page de configuration du design
 *
 * Interface admin pour personnaliser couleurs, typographies,
 * logo, favicon et CSS personnalisé.
 * Layout 2 colonnes : formulaire + aperçu en temps réel.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { apiFetch, apiUpload } from '../utils/api.js';
import { useTheme, useSettings } from '../contexts/ThemeContext.js';
import type { SiteSettings } from '../types/settings.js';

/** Champ de couleur avec color picker et input hex */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
      />
      <div className="flex-1">
        <label className="block text-xs font-medium text-text-light mb-0.5">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          pattern="^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$"
          className="w-full px-2 py-1 text-sm rounded border border-gray-200
                     font-mono text-text focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
      </div>
    </div>
  );
}

/** Sélecteur de police */
function FontSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const FONTS = ['Inter', 'Lato', 'Open Sans', 'Roboto', 'Playfair Display', 'Merriweather', 'Poppins', 'Montserrat'];

  return (
    <div>
      <label className="block text-xs font-medium text-text-light mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 text-text
                   focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {FONTS.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>
            {f}
          </option>
        ))}
      </select>
    </div>
  );
}

type FormData = Omit<SiteSettings, 'id' | 'updatedAt' | 'logoUrl' | 'faviconUrl'>;

export function SettingsPage() {
  const { refetch } = useTheme();
  const currentSettings = useSettings();

  const [form, setForm] = useState<FormData>({
    siteName: currentSettings.siteName,
    siteTagline: currentSettings.siteTagline,
    colorPrimary: currentSettings.colorPrimary,
    colorPrimaryLight: currentSettings.colorPrimaryLight,
    colorPrimaryPale: currentSettings.colorPrimaryPale,
    colorBackground: currentSettings.colorBackground,
    colorSurface: currentSettings.colorSurface,
    colorText: currentSettings.colorText,
    colorTextLight: currentSettings.colorTextLight,
    colorAccent1: currentSettings.colorAccent1,
    colorAccent2: currentSettings.colorAccent2,
    fontPrimary: currentSettings.fontPrimary,
    fontHeading: currentSettings.fontHeading,
    fontMono: currentSettings.fontMono,
    customCss: currentSettings.customCss,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Mettre à jour le formulaire quand les settings changent
  useEffect(() => {
    setForm({
      siteName: currentSettings.siteName,
      siteTagline: currentSettings.siteTagline,
      colorPrimary: currentSettings.colorPrimary,
      colorPrimaryLight: currentSettings.colorPrimaryLight,
      colorPrimaryPale: currentSettings.colorPrimaryPale,
      colorBackground: currentSettings.colorBackground,
      colorSurface: currentSettings.colorSurface,
      colorText: currentSettings.colorText,
      colorTextLight: currentSettings.colorTextLight,
      colorAccent1: currentSettings.colorAccent1,
      colorAccent2: currentSettings.colorAccent2,
      fontPrimary: currentSettings.fontPrimary,
      fontHeading: currentSettings.fontHeading,
      fontMono: currentSettings.fontMono,
      customCss: currentSettings.customCss,
    });
  }, [currentSettings]);

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    const res = await apiFetch('/settings', {
      method: 'PUT',
      body: JSON.stringify(form),
    });

    if (res.success) {
      await refetch();
      setMessage({ type: 'success', text: 'Paramètres enregistrés' });
    } else {
      setMessage({ type: 'error', text: res.error ?? 'Erreur lors de la sauvegarde' });
    }

    setIsSaving(false);
  }

  async function handleReset() {
    const res = await apiFetch('/settings/reset', { method: 'POST' });
    if (res.success) {
      await refetch();
      setMessage({ type: 'success', text: 'Paramètres réinitialisés' });
    }
  }

  async function handleUpload(field: 'logo' | 'favicon', file: File) {
    const formData = new FormData();
    formData.append(field, file);

    const res = await apiUpload(`/settings/${field}`, formData);
    if (res.success) {
      await refetch();
      setMessage({ type: 'success', text: `${field === 'logo' ? 'Logo' : 'Favicon'} mis à jour` });
    } else {
      setMessage({ type: 'error', text: res.error ?? 'Erreur lors de l\'upload' });
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-heading font-bold text-accent mb-6">Configuration du design</h1>

      {/* Message de feedback */}
      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Formulaire (3 colonnes) */}
        <form onSubmit={handleSave} className="lg:col-span-3 space-y-6">
          {/* Identité */}
          <section className="bg-surface rounded-xl p-5 border border-gray-100">
            <h2 className="flex items-center gap-2 text-base font-semibold text-accent mb-4">
              <i className="bi bi-card-heading" style={{ fontSize: 16 }} /> Identité
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Nom du site</label>
                <input
                  type="text"
                  value={form.siteName}
                  onChange={(e) => updateField('siteName', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-text
                             focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">Tagline</label>
                <input
                  type="text"
                  value={form.siteTagline}
                  onChange={(e) => updateField('siteTagline', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-text
                             focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Upload logo */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">Logo</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload('logo', file);
                  }}
                  className="w-full text-sm text-text-light file:mr-3 file:py-2 file:px-4
                             file:rounded-lg file:border-0 file:bg-primary/10 file:text-primary
                             file:font-medium file:cursor-pointer hover:file:bg-primary/20"
                />
              </div>

              {/* Upload favicon */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">Favicon</label>
                <input
                  type="file"
                  accept="image/png,image/x-icon,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload('favicon', file);
                  }}
                  className="w-full text-sm text-text-light file:mr-3 file:py-2 file:px-4
                             file:rounded-lg file:border-0 file:bg-primary/10 file:text-primary
                             file:font-medium file:cursor-pointer hover:file:bg-primary/20"
                />
              </div>
            </div>
          </section>

          {/* Palette de couleurs */}
          <section className="bg-surface rounded-xl p-5 border border-gray-100">
            <h2 className="flex items-center gap-2 text-base font-semibold text-accent mb-4">
              <i className="bi bi-palette" style={{ fontSize: 16 }} /> Palette de couleurs
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ColorField label="Primaire (doré)" value={form.colorPrimary} onChange={(v) => updateField('colorPrimary', v)} />
              <ColorField label="Primaire clair" value={form.colorPrimaryLight} onChange={(v) => updateField('colorPrimaryLight', v)} />
              <ColorField label="Primaire pâle" value={form.colorPrimaryPale} onChange={(v) => updateField('colorPrimaryPale', v)} />
              <ColorField label="Fond de page" value={form.colorBackground} onChange={(v) => updateField('colorBackground', v)} />
              <ColorField label="Surface (cartes)" value={form.colorSurface} onChange={(v) => updateField('colorSurface', v)} />
              <ColorField label="Texte principal" value={form.colorText} onChange={(v) => updateField('colorText', v)} />
              <ColorField label="Texte secondaire" value={form.colorTextLight} onChange={(v) => updateField('colorTextLight', v)} />
              <ColorField label="Accent (anthracite)" value={form.colorAccent1} onChange={(v) => updateField('colorAccent1', v)} />
              <ColorField label="Accent clair" value={form.colorAccent2} onChange={(v) => updateField('colorAccent2', v)} />
            </div>
          </section>

          {/* Typographie */}
          <section className="bg-surface rounded-xl p-5 border border-gray-100">
            <h2 className="flex items-center gap-2 text-base font-semibold text-accent mb-4">
              <i className="bi bi-fonts" style={{ fontSize: 16 }} /> Typographie
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FontSelect label="Police principale" value={form.fontPrimary} onChange={(v) => updateField('fontPrimary', v)} />
              <FontSelect label="Police des titres" value={form.fontHeading} onChange={(v) => updateField('fontHeading', v)} />
            </div>

            <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-100">
              <p style={{ fontFamily: `'${form.fontHeading}', sans-serif` }} className="text-lg font-semibold text-accent">
                Exemple de titre
              </p>
              <p style={{ fontFamily: `'${form.fontPrimary}', sans-serif` }} className="text-sm text-text-light mt-1">
                Voici un texte de paragraphe avec la police sélectionnée.
              </p>
            </div>
          </section>

          {/* CSS personnalisé */}
          <section className="bg-surface rounded-xl p-5 border border-gray-100">
            <h2 className="flex items-center gap-2 text-base font-semibold text-accent mb-4">
              <i className="bi bi-code-slash" style={{ fontSize: 16 }} /> CSS personnalisé
            </h2>

            <textarea
              value={form.customCss ?? ''}
              onChange={(e) => updateField('customCss', e.target.value || null)}
              maxLength={10000}
              rows={6}
              placeholder="/* Entrez votre CSS personnalisé ici */"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                         font-mono text-text placeholder:text-text-light
                         focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
            />
            <p className="text-xs text-text-light mt-1">
              {(form.customCss?.length ?? 0).toLocaleString()} / 10 000 caractères
            </p>
          </section>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-accent
                         font-semibold hover:bg-primary-light transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="bi bi-check-lg" style={{ fontSize: 14 }} />
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200
                         text-text-light hover:bg-gray-50 transition-colors text-sm"
            >
              <i className="bi bi-arrow-counterclockwise" style={{ fontSize: 14 }} />
              Réinitialiser
            </button>
          </div>
        </form>

        {/* Aperçu en temps réel (2 colonnes) */}
        <div className="lg:col-span-2">
          <div className="sticky top-6 space-y-4">
            <h3 className="text-sm font-semibold text-text-light uppercase tracking-wider">Aperçu</h3>

            {/* Mini header */}
            <div
              className="rounded-xl overflow-hidden border border-gray-200"
              style={{ backgroundColor: form.colorSurface }}
            >
              <div
                className="px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: form.colorAccent1 }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: form.colorPrimary, color: form.colorAccent1 }}
                >
                  A
                </div>
                <span className="text-sm font-medium text-white">{form.siteName}</span>
              </div>

              <div className="p-4 space-y-3">
                <h4
                  className="text-base font-semibold"
                  style={{ color: form.colorAccent1, fontFamily: `'${form.fontHeading}', sans-serif` }}
                >
                  Exemple de titre
                </h4>
                <p
                  className="text-sm"
                  style={{ color: form.colorTextLight, fontFamily: `'${form.fontPrimary}', sans-serif` }}
                >
                  {form.siteTagline}
                </p>

                {/* Carte exemple */}
                <div
                  className="rounded-lg p-3 border-l-4"
                  style={{
                    backgroundColor: form.colorBackground,
                    borderLeftColor: form.colorPrimary,
                  }}
                >
                  <p className="text-sm font-medium" style={{ color: form.colorText }}>
                    Soleil en Bélier
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: form.colorTextLight }}>
                    Maison I — Angulaire
                  </p>
                </div>

                {/* Bouton exemple */}
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: form.colorPrimary, color: form.colorAccent1 }}
                >
                  Bouton primaire
                </button>
              </div>
            </div>

            {/* Palette visuelle */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Primaire', color: form.colorPrimary },
                { label: 'Pri. clair', color: form.colorPrimaryLight },
                { label: 'Pri. pâle', color: form.colorPrimaryPale },
                { label: 'Fond', color: form.colorBackground },
                { label: 'Surface', color: form.colorSurface },
                { label: 'Texte', color: form.colorText },
                { label: 'Txt. léger', color: form.colorTextLight },
                { label: 'Accent', color: form.colorAccent1 },
                { label: 'Acc. clair', color: form.colorAccent2 },
              ].map((c) => (
                <div key={c.label} className="text-center">
                  <div
                    className="w-full h-8 rounded-md border border-gray-200"
                    style={{ backgroundColor: c.color }}
                  />
                  <p className="text-[10px] text-text-light mt-1">{c.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
